/**
 * V113: MM 조직 설정 조회 훅
 *
 * getMmOrgSettings callable을 호출하여 조직 목록과 MM 설정 상태를 조회합니다.
 *
 * @module shared/hooks/useMmOrgSettings
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useCurrentUser } from './useCurrentUser';
import {
  GetMmOrgSettingsRequest,
  GetMmOrgSettingsResponse,
  MmOrgSettingsItem,
} from '../types/mm';

/**
 * MM 조직 설정 조회 훅 옵션
 */
export interface UseMmOrgSettingsOptions {
  /** 회사 코드 (KR/CN) */
  companyCode?: 'KR' | 'CN';
  /** V235: false면 CF 호출 스킵 (권한 없는 역할의 403 방지) */
  enabled?: boolean;
}

/**
 * MM 조직 설정 조회 훅 반환 타입
 */
export interface UseMmOrgSettingsResult {
  /** 조직 목록 */
  orgUnits: MmOrgSettingsItem[];

  /** 로딩 상태 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 새로고침 */
  refetch: () => Promise<void>;
}

/**
 * MM 조직 설정 조회 훅
 *
 * @param options 옵션
 * @returns 조직 목록 및 상태
 */
export function useMmOrgSettings(
  options: UseMmOrgSettingsOptions = {}
): UseMmOrgSettingsResult {
  const { user } = useCurrentUser();
  const enabled = options.enabled !== false; // 기본 true
  const companyCode =
    options.companyCode || (user?.profile?.companyCode as 'KR' | 'CN') || 'KR';

  const [orgUnits, setOrgUnits] = useState<MmOrgSettingsItem[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    // V235: enabled=false면 CF 호출 스킵
    if (!enabled || !user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // V113.1: firebase/config.ts에서 초기화된 functions 인스턴스 사용 (CORS 방지)
      if (!functions) {
        throw new Error('Firebase Functions가 초기화되지 않았습니다.');
      }

      const getMmOrgSettings = httpsCallable<
        GetMmOrgSettingsRequest,
        GetMmOrgSettingsResponse
      >(functions, 'getMmOrgSettings');

      const result = await getMmOrgSettings({ companyCode });

      if (result.data.success) {
        setOrgUnits(result.data.orgUnits);
      } else {
        throw new Error(result.data.error || 'MM 조직 설정 조회 실패');
      }
    } catch (err: any) {
      console.error('[V113] useMmOrgSettings error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user, companyCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    orgUnits,
    isLoading,
    error,
    refetch: fetchData,
  };
}
